import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, Collapse, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, IconButton, Menu, MenuItem, Switch, TextField, Tooltip, Typography, useMediaQuery,
} from '@mui/material';
import {
  AccountBalanceWallet, Add, Block, CheckCircle, ChevronLeft, ChevronRight, Close, Description, Edit, EventRepeat, ExpandLess, ExpandMore,
  FileDownload, FilterList, Fullscreen, FullscreenExit, Gavel, InfoOutlined, LocalHospital, Payments, Percent, Print, Receipt, ReceiptLong, Restore, Undo,
} from '@mui/icons-material';
import { DiscountModal, HealthPlanModal, PeriodicityModal } from './PatientFinancialBlockModals';
import AppointmentEditorDialog from './AppointmentEditorDialog';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/useToast';
import { Toast } from './Toast';
import { patientsService } from '../services/patientsService';
import ClearFiltersButton from './ClearFiltersButton';
import MonthSelect from './Inputs/MonthSelect';
import { colors } from '../theme/designSystem';
import { dateFieldSx, filterActionButtonSx, filterFieldSx } from '../theme/filterStyles';
import { PATIENT_FINANCIAL_PRINT_KEY, FinancialPrintPayload } from './PatientFinancialPrint';
import {
  FinancialPatientDiscount, FinancialPayment, FinancialSession, PatientFinancial, PatientHealthPlanLink, PaymentMethodOption, patientFinancialService,
} from '../services/patientFinancialService';

/* ============================================================================
   Financeiro do paciente — tela de caixa (05/09/2026). Usada na aba do cadastro e na página
   "Financeiro do paciente" do menu Financeiro: UM componente, duas entradas.
   Sessões que cobram (ou já pagas) vêm de api/patient-financial; o pagamento cria o recibo,
   dá baixa nas sessões e abre o recibo para impressão no template da entidade.
   ============================================================================ */

const brl = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const fmtDate = (isoDate: string) => {
  const [y, m, d] = isoDate.slice(0, 10).split('-');
  return `${d}/${m}/${y}`;
};
const round2 = (v: number) => Math.round(v * 100) / 100;
/**
 * Hoje em AAAA-MM-DD pelo relogio do aparelho. O servidor confere pelo relogio da ENTIDADE e
 * recusa data futura (RN-543j) -- aqui o teto so evita a viagem inutil.
 */
const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
/**
 * Piso da data do pagamento (RN-543j): 30 dias. Serve para regularizar baixa atrasada sem
 * reabrir competencia antiga -- relatorio de Recebimentos ja fechado nao muda de numero porque
 * alguem corrigiu a data meses depois. O servidor recusa igual.
 */
const PAYMENT_DATE_MAX_PAST_DAYS = 30;
const oldestPaymentIso = () => {
  const d = new Date();
  d.setDate(d.getDate() - PAYMENT_DATE_MAX_PAST_DAYS);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const timeOf = (isoDate: string) => (isoDate.length >= 16 ? isoDate.slice(11, 16) : '');

/** Campo monetário: aceita "1.234,56", "1234.56", "-20"; NaN quando não é número. */
const parseMoney = (raw: string): number => {
  const t = raw.trim();
  if (t === '' || t === '-') return 0;
  const normalized = t.includes(',') ? t.replace(/\./g, '').replace(',', '.') : t;
  return Number(normalized);
};
const formatMoney = (v: number) => v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
/** Máscara leve: mantém só o que compõe um valor enquanto digita; formata com 2 casas no blur. */
const moneyKeystroke = (raw: string) => raw.replace(/[^0-9.,-]/g, '').replace(/(?!^)-/g, '');
const moneyBlur = (raw: string, allowNegative: boolean): string => {
  const n = parseMoney(raw);
  if (Number.isNaN(n) || raw.trim() === '') return raw.trim() === '' ? '' : raw;
  return formatMoney(allowNegative ? n : Math.abs(n));
};

/** Rateio em centavos com o resíduo na última sessão — espelho do servidor (MoneySplit). */
export const splitAmount = (total: number, weights: number[]): number[] => {
  const cents = Math.round(total * 100);
  const sumW = weights.reduce((s, w) => s + w, 0);
  if (weights.length === 0) return [];
  const parts = sumW <= 0
    ? weights.map(() => Math.floor(cents / weights.length))
    : weights.map((w) => Math.floor((cents * w) / sumW));
  const rest = cents - parts.reduce((s, p) => s + p, 0);
  parts[parts.length - 1] += rest;
  return parts.map((p) => p / 100);
};

const apiError = (err: unknown, fallback: string): string => {
  const e = err as { response?: { data?: { description?: string; message?: string; errors?: { description?: string }[] } } };
  const d = e?.response?.data;
  return d?.description || d?.message || d?.errors?.[0]?.description || fallback;
};

// Tudo que não foi pago é Pendente, sessão futura incluída (Hilton, 05/09) — menos o que ficou
// antes da data de corte financeira (RN-543h): histórico, não é dívida
type SituationKey = 'paid' | 'pending' | 'locked';
const situationOf = (s: FinancialSession): SituationKey =>
  (s.paid ? 'paid' : s.financiallyLocked ? 'locked' : 'pending');
const SITUATION: Record<SituationKey, { label: string; bg: string; color: string }> = {
  paid: { label: 'Pago', bg: colors.successSoft, color: '#155724' },
  pending: { label: 'Pendente', bg: colors.warningSoft, color: colors.warningSoftText },
  locked: { label: 'Anterior ao corte', bg: colors.borderLight, color: colors.textSecondary },
};
// Altura real do campo: global.css força 45px no OutlinedInput (o token `inputs.default.height` diz 40).
// Os botões do caixa usam esta, senão ficam mais baixos que os campos ao lado.
const FIELD_HEIGHT = 45;

const CUTOFF_TIP = 'Anterior à data de corte financeira: só leitura';

const openReceipt = (paymentId: string, autoPrint: boolean) => {
  const url = `${window.location.origin}${window.location.pathname}?page=payment-receipt&id=${paymentId}${autoPrint ? '&print=1' : ''}`;
  window.open(url, '_blank', 'noopener');
};

interface Props { patientId: string; }

const PatientFinancialTab: React.FC<Props> = ({ patientId }) => {
  // Painel lateral (300px) + tabela só cabem lado a lado em tela larga; abaixo disso — celular deitado,
  // tablet — o painel vai para cima e a tabela ganha a largura toda, senão as colunas se sobrepõem
  const stackPanel = useMediaQuery('(max-width:1280px)');
  const { user } = useAuth();
  // Editor do agendamento (mesmo modal do calendário), aberto pelo ícone da linha
  const [editingScheduleId, setEditingScheduleId] = useState<string | null>(null);
  const [patientName, setPatientName] = useState('');
  useEffect(() => {
    patientsService.getById(patientId).then((p) => setPatientName(p.patientName)).catch(() => setPatientName(''));
  }, [patientId]);

  const [data, setData] = useState<PatientFinancial | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { toast, showToast, hideToast } = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setData(await patientFinancialService.getByPatient(patientId));
    } catch (err) {
      setError(apiError(err, 'Não foi possível carregar o financeiro do paciente.'));
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => { load(); }, [load]);

  // Filtros — padrão: competência ATUAL (mês corrente) e situação Pendente (Hilton, 05/09).
  // O bloco Pendências do painel não depende do filtro.
  const now = new Date();
  const localIso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const defaultFrom = localIso(new Date(now.getFullYear(), now.getMonth(), 1));
  const monthEnd = localIso(new Date(now.getFullYear(), now.getMonth() + 1, 0));
  const DEFAULT_SITUATION: SituationKey = 'pending';
  const [from, setFrom] = useState(defaultFrom);
  const [to, setTo] = useState(monthEnd);
  const [professional, setProfessional] = useState('');
  const [situation, setSituation] = useState<'' | SituationKey>(DEFAULT_SITUATION);
  // RN-228: filtro "só pacotes" e agrupamento por pacote (preferência lembrada no navegador; nasce desligado)
  const [onlyPackages, setOnlyPackages] = useState(false);
  const [groupByPackage, setGroupByPackage] = useState<boolean>(() => { try { return localStorage.getItem('c4u.fin.groupByPackage') === '1'; } catch { return false; } });
  const toggleGroupByPackage = () => setGroupByPackage((v) => { try { localStorage.setItem('c4u.fin.groupByPackage', v ? '0' : '1'); } catch { /* sem storage */ } return !v; });
  const hasFilters = from !== defaultFrom || to !== monthEnd || professional !== '' || situation !== DEFAULT_SITUATION;
  const clearFilters = () => { setFrom(defaultFrom); setTo(monthEnd); setProfessional(''); setSituation(DEFAULT_SITUATION); };

  // Competência (MonthSelect, o mesmo calendário dos relatórios): preenche De/Até com o mês
  // inteiro; mexer nas datas à mão desfaz a escolha (a competência deriva das datas)
  const competenceOf = (f: string, t: string): string => {
    if (f.length < 10 || t.length < 10 || f.slice(0, 7) !== t.slice(0, 7) || !f.endsWith('-01')) return '';
    const [y, m] = f.split('-').map(Number);
    return t === localIso(new Date(y, m, 0)) ? f.slice(0, 7) : '';
  };
  const competence = competenceOf(from, to);
  const applyCompetence = (value: string) => {
    if (!value) return;
    const [y, m] = value.split('-').map(Number);
    setFrom(localIso(new Date(y, m - 1, 1)));
    setTo(localIso(new Date(y, m, 0)));
  };

  const [selected, setSelected] = useState<string[]>([]);
  // Mudou qualquer filtro, a seleção zera: o que está marcado é sempre o que está em tela
  useEffect(() => { setSelected([]); }, [from, to, professional, situation]);
  const [generalDiscountId, setGeneralDiscountId] = useState('');
  const [manualAdjust, setManualAdjust] = useState('');
  const [manualJustification, setManualJustification] = useState('');
  // Painel lateral nasce aberto em qualquer tela (Hilton, 05/09): recolhido, a situação do paciente ficava escondida
  const [sideOpen, setSideOpen] = useState(true);
  // Tela cheia do painel: os blocos ocupam a largura toda e a tabela do caixa se esconde
  const [sideFull, setSideFull] = useState(false);
  // Na lateral só Pendências nasce aberto (Hilton, 05/09); em tela cheia tudo abre. O clique no título fecha/abre cada um
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({});
  const isSectionOpen = (key: string) => openSections[key] ?? key === 'pending';
  const [payOpen, setPayOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [revertTarget, setRevertTarget] = useState<string | null>(null);
  const [editingPayment, setEditingPayment] = useState<FinancialPayment | null>(null);
  // CRUD dos blocos (RN-543g)
  const [discountModal, setDiscountModal] = useState<{ open: boolean; editing: FinancialPatientDiscount | null }>({ open: false, editing: null });
  const [periodicityOpen, setPeriodicityOpen] = useState(false);
  const [healthPlanOpen, setHealthPlanOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState<PatientHealthPlanLink | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const runBlockAction = async (id: string, action: () => Promise<void>, okText: string) => {
    setBusyId(id);
    try {
      await action();
      showToast(okText, 'success');
      await load();
    } catch (err) {
      showToast(apiError(err, 'Não foi possível concluir.'), 'error');
    } finally {
      setBusyId(null);
    }
  };

  const sessions = data?.sessions ?? [];
  const filtered = useMemo(() => sessions
    .filter((s) => (!from || s.date.slice(0, 10) >= from) && (!to || s.date.slice(0, 10) <= to))
    .filter((s) => !professional || s.professionalId === professional)
    .filter((s) => !situation || situationOf(s) === situation)
    .filter((s) => !onlyPackages || s.packagePosition != null || s.packageUnlinked)
    .sort((a, b) => a.date.localeCompare(b.date)), [sessions, from, to, professional, situation, onlyPackages]);
  // Agrupar por pacote: as sessões da mesma série ficam juntas, na ordem da 1ª sessão de cada pacote; avulsas no fim
  const rowsToRender = useMemo(() => {
    if (!groupByPackage) return filtered.map((s) => ({ session: s, groupHeader: null as null | { key: string; label: string; subtotal: number; count: number; done: number } }));
    const groups = new Map<string, FinancialSession[]>();
    const loose: FinancialSession[] = [];
    for (const s of filtered) { if (s.packageParentId) { const g = groups.get(s.packageParentId) ?? []; g.push(s); groups.set(s.packageParentId, g); } else loose.push(s); }
    const out: { session: FinancialSession; groupHeader: null | { key: string; label: string; subtotal: number; count: number; done: number } }[] = [];
    groups.forEach((g: FinancialSession[], key: string) => {
      const size = g[0].packageSize ?? g.length;
      const done = g.filter((x: FinancialSession) => x.attended).length;
      const label = `${g[0].serviceTypeName || 'Pacote'} · ${g.length} de ${size} em tela · ${done} realizada(s)`;
      g.forEach((s: FinancialSession, i: number) => out.push({ session: s, groupHeader: i === 0 ? { key, label, subtotal: round2(g.reduce((t: number, x: FinancialSession) => t + x.netValue, 0)), count: g.length, done } : null }));
    });
    loose.forEach((s) => out.push({ session: s, groupHeader: null }));
    return out;
  }, [filtered, groupByPackage]);

  const payable = filtered.filter((s) => !s.paid && !s.financiallyLocked);
  const selectedRows = useMemo(
    () => sessions.filter((s) => selected.includes(s.id) && !s.paid && !s.financiallyLocked).sort((a, b) => a.date.localeCompare(b.date)),
    [sessions, selected]);
  // Pagamento com sessão anterior ao corte não altera nem estorna (o servidor também recusa)
  const lockedPaymentIds = useMemo(
    () => new Set(sessions.filter((s) => s.financiallyLocked && s.paymentId).map((s) => s.paymentId!)),
    [sessions]);

  const selectedTotal = round2(selectedRows.reduce((t, s) => t + s.netValue, 0));
  // Desconto geral não se acumula com desconto do paciente já embutido em alguma sessão selecionada
  const hasPatientDiscountSelected = selectedRows.some((s) => s.patientDiscount > 0);
  useEffect(() => { if (hasPatientDiscountSelected && generalDiscountId) setGeneralDiscountId(''); }, [hasPatientDiscountSelected, generalDiscountId]);
  const generalDiscount = data?.generalDiscounts.find((g) => g.id === generalDiscountId);
  const generalDiscountValue = generalDiscount ? round2(selectedTotal * generalDiscount.percent / 100) : 0;
  const manual = parseMoney(manualAdjust);
  const manualValid = !Number.isNaN(manual);
  // Cenário 2 (RN-543b): percentual geral OU ajuste manual, nunca os dois
  const adjustInUse = manualValid && manual !== 0;
  const percentInUse = generalDiscountId !== '';
  const afterGeneral = round2(selectedTotal - generalDiscountValue);
  const totalToPay = round2(afterGeneral + (manualValid ? manual : 0));
  // Teto: desconto até o total selecionado; acréscimo até o dobro
  const manualError = !manualValid
    ? 'Informe um número.'
    : manual < 0 && -manual > afterGeneral
      ? 'O desconto não pode passar do total selecionado.'
      : manual > 0 && manual > afterGeneral * 2
        ? 'O acréscimo não pode passar do dobro do total selecionado.'
        : '';
  // Ajuste manual exige justificativa (mínimo 5 caracteres) — sem ela o pagamento não habilita
  const MIN_JUSTIFICATION = 5;
  const needsJustification = manualValid && manual !== 0;
  const justificationOk = !needsJustification || manualJustification.trim().length >= MIN_JUSTIFICATION;
  const canPay = selectedRows.length > 0 && !manualError && totalToPay >= 0 && justificationOk;

  // Prévia do rateio por sessão (mesma regra do servidor): valor final e ajuste de cada linha
  const previewFinal = useMemo(() => {
    const finals = splitAmount(Math.max(0, totalToPay), selectedRows.map((s) => s.netValue));
    const map = new Map<string, number>();
    selectedRows.forEach((s, i) => map.set(s.id, finals[i] ?? s.netValue));
    return map;
  }, [selectedRows, totalToPay]);

  const toggle = (id: string) => setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const allPayableSelected = payable.length > 0 && payable.every((s) => selected.includes(s.id));
  const togglePage = () => setSelected((prev) => (allPayableSelected
    ? prev.filter((id) => !payable.some((s) => s.id === id))
    : Array.from(new Set([...prev, ...payable.map((s) => s.id)]))));

  const toggleSection = (key: string) => setOpenSections((p) => ({ ...p, [key]: !(p[key] ?? key === 'pending') }));
  const openSide = (key: string) => { setSideOpen(true); setOpenSections((p) => ({ ...p, [key]: true })); };

  const resetCashier = () => { setSelected([]); setGeneralDiscountId(''); setManualAdjust(''); setManualJustification(''); };

  // Extrato (decisão do Hilton, 13/09): o botão abre um menu com três recortes — todas do período
  // (só o filtro de datas), todas da tela (filtros atuais) ou as selecionadas. Os dados vão pelo
  // localStorage: a aba nova abre com noopener e NÃO recebe cópia do sessionStorage (saía vazio).
  const [printMenuAnchor, setPrintMenuAnchor] = useState<HTMLElement | null>(null);
  const periodLabel = (from || to) ? `${from ? fmtDate(from) : 'início'} a ${to ? fmtDate(to) : 'hoje'}` : 'todo o histórico';
  const printRows = (scope: 'period' | 'screen' | 'selected') => {
    setPrintMenuAnchor(null);
    let rows: FinancialSession[];
    let label: string;
    if (scope === 'period') {
      rows = sessions
        .filter((s) => (!from || s.date.slice(0, 10) >= from) && (!to || s.date.slice(0, 10) <= to))
        .sort((a, b) => a.date.localeCompare(b.date));
      label = `Todas do período (${periodLabel})`;
    } else if (scope === 'screen') {
      rows = filtered;
      const parts: string[] = [periodLabel];
      if (professional) parts.push(data?.professionals.find((p) => p.id === professional)?.name ?? '');
      parts.push(situation ? SITUATION[situation].label : 'Todas as situações');
      label = `Todas da tela (${parts.filter(Boolean).join(' · ')})`;
    } else {
      rows = filtered.filter((s) => selected.includes(s.id));
      label = `Sessões selecionadas (${rows.length})`;
    }
    const payload: FinancialPrintPayload = { filtersLabel: label, rows };
    try { localStorage.setItem(PATIENT_FINANCIAL_PRINT_KEY, JSON.stringify(payload)); } catch { /* sem storage: a impressão avisa */ }
    window.open(`${window.location.origin}${window.location.pathname}?page=patient-financial-print&patientId=${patientId}`, '_blank', 'noopener');
  };

  const exportFiltered = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const blob = await patientFinancialService.exportXlsx(patientId, { from, to, professionalId: professional, situation });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `financeiro_paciente_${from || 'inicio'}_${to || 'hoje'}.xlsx`;
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      showToast('Não foi possível exportar a planilha. Tente novamente.', 'error');
    } finally {
      setExporting(false);
    }
  };

  const handlePay = async (method1Id: string | null, value1: number | null, method2Id: string | null, value2: number | null, notes: string, byInjunction: boolean, invoiceUrl: string, printReceipt: boolean, paymentDate: string) => {
    const receipt = await patientFinancialService.pay({
      patientId,
      scheduleIds: selectedRows.map((s) => s.id),
      generalDiscountId: generalDiscountId || null,
      manualAdjust: manualValid ? manual : 0,
      manualJustification: needsJustification ? manualJustification.trim() : null,
      method1Id, value1, method2Id, value2,
      notes: notes.trim() || null,
      byInjunction,
      invoiceUrl: invoiceUrl.trim() || null,
      paymentDate: paymentDate || null,
    });
    setPayOpen(false);
    resetCashier();
    showToast(`Pagamento de ${brl(receipt.total)} registrado.${printReceipt ? ' O recibo abriu em outra aba.' : ''}`, 'success');
    if (printReceipt) openReceipt(receipt.paymentId, true);
    await load();
  };

  const handleUpdatePayment = async (method1Id: string | null, value1: number | null, method2Id: string | null, value2: number | null, notes: string, _byInjunction: boolean, invoiceUrl: string, _printReceipt: boolean, paymentDate: string) => {
    if (!editingPayment) return;
    await patientFinancialService.updateMethods(editingPayment.id, { method1Id, value1, method2Id, value2, notes: notes.trim() || null, invoiceUrl: invoiceUrl.trim() || null, paymentDate: paymentDate || null });
    setEditingPayment(null);
    showToast('Pagamento atualizado.', 'success');
    await load();
  };

  const handleRevert = async (justification: string) => {
    if (!revertTarget) return;
    await patientFinancialService.revert(revertTarget, justification);
    setRevertTarget(null);
    showToast('Pagamento estornado; as sessões voltaram a ficar em aberto.', 'success');
    await load();
  };

  if (loading && !data) {
    return (
      <div className="tab-content-section">
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress sx={{ color: colors.primary }} /></Box>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="tab-content-section">
        <Alert severity="error" action={<Button size="small" onClick={load} sx={{ textTransform: 'none' }}>Tentar de novo</Button>}>{error}</Alert>
      </div>
    );
  }

  const d = data!;
  const activePayments = d.payments.filter((p) => !p.reverted).length;

  /* ---------------- painel lateral ---------------- */
  const headerAction = (title: string, onClick: () => void, icon: React.ReactNode = <Add sx={{ fontSize: 18 }} />) => (
    <Tooltip title={title} arrow>
      <IconButton size="small" onClick={(e) => { e.stopPropagation(); onClick(); }} sx={{ color: colors.primary, p: 0.25 }}>
        {icon}
      </IconButton>
    </Tooltip>
  );
  // Atalho do bloco Pendências (Hilton, 05/09): a grade passa a mostrar exatamente as sessões que compõem o valor
  const showPendingInGrid = () => {
    if (!d.pendingSince) return;
    setFrom(d.pendingSince.slice(0, 10));
    setTo(todayLocal);
    setProfessional('');
    setSituation('pending');
    if (fullMode) setSideFull(false);
  };
  const rowIcon = (title: string, icon: React.ReactNode, onClick: () => void, color: string, disabled = false) => (
    <Tooltip title={title} arrow>
      <span>
        <IconButton size="small" onClick={onClick} disabled={disabled} sx={{ color, p: 0.5 }}>{icon}</IconButton>
      </span>
    </Tooltip>
  );
  const fullMode = sideOpen && sideFull;
  const todayLocal = localIso(new Date());
  const isExpired = (x: FinancialPatientDiscount) => !!x.to && x.to.slice(0, 10) < todayLocal;
  const activeDiscounts = d.patientDiscounts.filter((x) => x.active && !isExpired(x));
  const lastPayment = d.payments.find((p) => !p.reverted) ?? null;
  const sections: { key: string; icon: React.ReactNode; title: string; badge?: string; action?: React.ReactNode; body: React.ReactNode }[] = [
    {
      key: 'pending', icon: <AccountBalanceWallet fontSize="small" />, title: 'Pendências', badge: d.pendingTotal > 0 ? brl(d.pendingTotal) : 'Sem pendências',
      action: d.pendingTotal > 0 ? headerAction('Ver na grade as sessões que compõem este valor', showPendingInGrid, <FilterList sx={{ fontSize: 18 }} />) : undefined,
      body: (
        <>
          {d.pendingTotal > 0 ? (
            <>
              <Typography className="fin-keep" sx={{ fontSize: fullMode ? '2rem' : '1.5rem', fontWeight: 700, lineHeight: 1.2, color: colors.error }}>{brl(d.pendingTotal)}</Typography>
              <Typography sx={{ fontSize: '0.8rem', color: colors.textSecondary }}>
                {`${d.pendingCount} sessão(ões) cobrável(is) desde ${fmtDate(d.pendingSince!)} até hoje.`}
              </Typography>
              {(d.pendingPackageTotal ?? 0) > 0 && (
                <Typography sx={{ fontSize: '0.8rem', color: colors.textSecondary }}>
                  {`Deste valor, ${brl(d.pendingPackageTotal!)} são de pacotes.`}
                </Typography>
              )}
            </>
          ) : (
            <Chip label="Sem pendências" size="small" sx={{ bgcolor: colors.successSoft, color: '#155724', fontWeight: 600 }} />
          )}
          {d.financialCutoffDate && (
            <Typography sx={{ fontSize: '0.75rem', color: colors.textSecondary, mt: 0.5 }}>
              {`Data de corte financeira: ${fmtDate(d.financialCutoffDate)}. Atendimentos anteriores não entram na pendência.`}
            </Typography>
          )}
          <Box sx={{ mt: 1, pt: 1, borderTop: `1px solid ${colors.borderLight}`, display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 1, rowGap: 0.25, fontSize: '0.85rem' }}>
            <span style={{ color: colors.textSecondary }}>Último pagamento</span>
            <strong>{lastPayment ? fmtDate(lastPayment.date) : '—'}</strong>
            <span style={{ color: colors.textSecondary }}>Valor</span>
            <strong>{lastPayment ? brl(lastPayment.total) : '—'}</strong>
          </Box>
        </>
      ),
    },
    {
      key: 'periodicity', icon: <EventRepeat fontSize="small" />, title: 'Período de pag.', badge: d.periodicity ?? undefined,
      action: headerAction('Definir periodicidade', () => setPeriodicityOpen(true)),
      body: (
        <>
          <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>{d.periodicity ?? 'Não definida'}</Typography>
          {d.periodicityHistory.filter((p) => !p.active).length > 0 && (
            <Typography sx={{ fontSize: '0.72rem', color: colors.textSecondary, mt: 0.5 }}>
              Histórico: {d.periodicityHistory.filter((p) => !p.active).map((p) => `${p.period} (até ${fmtDate(p.createdAt)})`).join(' · ')}
            </Typography>
          )}
        </>
      ),
    },
    {
      key: 'payments', icon: <Payments fontSize="small" />, title: 'Pagamentos realizados', badge: String(activePayments),
      body: d.payments.length === 0
        ? <Typography sx={{ fontSize: '0.8rem', color: colors.textSecondary }}>Nenhum pagamento registrado.</Typography>
        : (
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {d.payments.map((p) => (
              <Box key={p.id} sx={{ display: 'flex', justifyContent: 'space-between', gap: 0.5, alignItems: 'center', opacity: p.reverted ? 0.55 : 1, py: 0.75, borderBottom: `1px solid ${colors.borderLight}`, '&:last-child': { borderBottom: 0 } }}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, lineHeight: 1.3, textDecoration: p.reverted ? 'line-through' : 'none' }}>{fmtDate(p.date)} · {brl(p.total)}</Typography>
                  <Typography className="fin-sub" sx={{ fontSize: '0.72rem', lineHeight: 1.3, color: colors.textSecondary }}>
                    {p.byInjunction ? 'Liminar' : (p.method1Name || '—')}{p.method2Name ? ` + ${p.method2Name}` : ''} · {p.sessions} sessão(ões){p.reverted ? ' · estornado' : ''}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexShrink: 0 }}>
                  <Tooltip title={p.reverted ? 'Estornado: não edita' : lockedPaymentIds.has(p.id) ? CUTOFF_TIP : p.byInjunction ? 'Pago por liminar: sem forma de pagamento' : 'Alterar forma de pagamento / observação'} arrow>
                    <span>
                      <IconButton size="small" disabled={p.reverted || p.byInjunction || lockedPaymentIds.has(p.id)} onClick={() => setEditingPayment(p)} sx={{ color: colors.primary, p: 0.5 }}><Edit sx={{ fontSize: 18 }} /></IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Recibo" arrow><IconButton size="small" onClick={() => openReceipt(p.id, false)} sx={{ color: colors.primary, p: 0.5 }}><Receipt sx={{ fontSize: 18 }} /></IconButton></Tooltip>
                  <Tooltip title={p.invoiceUrl ? 'Abrir nota fiscal' : 'Sem link de NF'} arrow>
                    <span>
                      <IconButton size="small" disabled={!p.invoiceUrl} onClick={() => p.invoiceUrl && window.open(p.invoiceUrl, '_blank', 'noopener')} sx={{ color: colors.textSecondary, p: 0.5 }}><ReceiptLong sx={{ fontSize: 18 }} /></IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title={p.reverted ? 'Já estornado' : lockedPaymentIds.has(p.id) ? CUTOFF_TIP : 'Estornar'} arrow>
                    <span>
                      <IconButton size="small" disabled={p.reverted || lockedPaymentIds.has(p.id)} onClick={() => setRevertTarget(p.id)} sx={{ color: colors.error, p: 0.5 }}><Undo sx={{ fontSize: 18 }} /></IconButton>
                    </span>
                  </Tooltip>
                </Box>
              </Box>
            ))}
          </Box>
        ),
    },
    {
      key: 'discounts', icon: <Percent fontSize="small" />, title: 'Descontos programados', badge: String(activeDiscounts.length),
      action: headerAction('Novo desconto programado', () => setDiscountModal({ open: true, editing: null })),
      body: d.patientDiscounts.length === 0
        ? <Typography sx={{ fontSize: '0.8rem', color: colors.textSecondary }}>Nenhum desconto cadastrado. Use o + para incluir.</Typography>
        : (
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {d.patientDiscounts.map((x) => (
              <Box key={x.id} sx={{ display: 'flex', justifyContent: 'space-between', gap: 0.5, alignItems: 'center', opacity: x.active ? 1 : 0.55, py: 0.75, borderBottom: `1px solid ${colors.borderLight}`, '&:last-child': { borderBottom: 0 } }}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, lineHeight: 1.3, textDecoration: x.active ? 'none' : 'line-through' }}>{brl(x.value)} por atendimento</Typography>
                  <Typography className="fin-sub" sx={{ fontSize: '0.72rem', lineHeight: 1.3, color: colors.textSecondary }}>
                    {[x.serviceTypeName, x.professionalTypeName, x.professionalNames, x.teamName].filter(Boolean).join(' · ') || 'Qualquer atendimento'}
                    {x.from || x.to ? ` · ${x.from ? fmtDate(x.from) : '…'} a ${x.to ? fmtDate(x.to) : '…'}` : ''}
                    {x.comments ? ` · ${x.comments}` : ''}{x.active ? '' : ' · inativo'}{isExpired(x) ? ' · expirado' : ''}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexShrink: 0 }}>
                  {/* Só desconto ativo e dentro da vigência pode ser alterado (Hilton, 05/09) */}
                  {rowIcon(!x.active ? 'Inativo: reative para alterar' : isExpired(x) ? 'Expirado: não pode ser alterado' : 'Editar', <Edit sx={{ fontSize: 16 }} />,
                    () => setDiscountModal({ open: true, editing: x }), colors.primary, busyId === x.id || !x.active || isExpired(x))}
                  {x.active
                    ? rowIcon('Inativar', <Block sx={{ fontSize: 16 }} />, () => runBlockAction(x.id, () => patientFinancialService.setDiscountActive(x.id, false), 'Desconto inativado.'), colors.error, busyId === x.id)
                    : rowIcon(isExpired(x) ? 'Expirado: não reativa' : 'Reativar', <Restore sx={{ fontSize: 16 }} />, () => runBlockAction(x.id, () => patientFinancialService.setDiscountActive(x.id, true), 'Desconto reativado.'), colors.success, busyId === x.id || isExpired(x))}
                </Box>
              </Box>
            ))}
          </Box>
        ),
    },
    {
      key: 'health', icon: <LocalHospital fontSize="small" />, title: 'Convênios', badge: String(d.healthPlans.filter((h) => h.active).length),
      action: headerAction('Vincular convênio', () => { setEditingPlan(null); setHealthPlanOpen(true); }),
      body: d.healthPlans.length === 0
        ? <Typography sx={{ fontSize: '0.8rem', color: colors.textSecondary }}>Nenhum convênio vinculado. Use o + para vincular.</Typography>
        : (
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {d.healthPlans.map((h) => (
              <Box key={h.id} sx={{ display: 'flex', justifyContent: 'space-between', gap: 0.5, alignItems: 'center', opacity: h.active ? 1 : 0.55, py: 0.75, borderBottom: `1px solid ${colors.borderLight}`, '&:last-child': { borderBottom: 0 } }}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, lineHeight: 1.3, textDecoration: h.active ? 'none' : 'line-through' }}>{h.name}</Typography>
                  <Typography className="fin-sub" sx={{ fontSize: '0.72rem', lineHeight: 1.3, color: colors.textSecondary }}>
                    {h.refundable ? 'Reembolsável' : 'Sem reembolso'}{h.createdAt ? ` · vinculado em ${fmtDate(h.createdAt)}` : ''}{h.active ? '' : ' · encerrado'}
                  </Typography>
                  <Typography className="fin-sub" sx={{ fontSize: '0.72rem', lineHeight: 1.3, color: colors.textSecondary }}>
                    {d.healthPlanDataVisible
                      ? [
                          h.cardNumber ? `Carteirinha ${h.cardNumber}` : null,
                          h.cardValidUntil ? `válida até ${fmtDate(h.cardValidUntil)}` : null,
                          h.isHolder === null ? null : h.isHolder ? 'titular' : `dependente de ${h.holderName ?? ''}`,
                        ].filter(Boolean).join(' · ') || 'Sem carteirinha informada'
                      : 'Carteirinha restrita ao seu perfil'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexShrink: 0 }}>
                  {rowIcon(h.active ? 'Editar vínculo' : 'Encerrado: reative para editar', <Edit sx={{ fontSize: 16 }} />,
                    () => { setEditingPlan(h); setHealthPlanOpen(true); }, colors.primary, busyId === h.id || !h.active)}
                  {h.active
                    ? rowIcon('Encerrar vínculo', <Block sx={{ fontSize: 16 }} />, () => runBlockAction(h.id, () => patientFinancialService.setHealthPlanActive(h.id, false), 'Vínculo encerrado.'), colors.error, busyId === h.id)
                    : rowIcon('Reativar vínculo', <Restore sx={{ fontSize: 16 }} />, () => runBlockAction(h.id, () => patientFinancialService.setHealthPlanActive(h.id, true), 'Vínculo reativado.'), colors.success, busyId === h.id)}
                </Box>
              </Box>
            ))}
          </Box>
        ),
    },
    // Contratos fica fora do painel até ser homologado (Hilton, 05/09); o plano de saúde voltou com a RN-411c (16/09)
    ...(d.injunction ? [{
      key: 'injunction', icon: <Gavel fontSize="small" />, title: 'Liminar judicial', badge: d.injunction.activeNow ? 'vigente' : 'encerrada',
      body: <Typography sx={{ fontSize: '0.85rem' }}>{d.injunction.title}{d.injunction.from ? ` · ${fmtDate(d.injunction.from)}` : ''}{d.injunction.to ? ` a ${fmtDate(d.injunction.to)}` : ''}</Typography>,
    }] : []),
  ];

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const hiddenBlocks: typeof sections = [
    {
      key: 'contracts', icon: <Description fontSize="small" />, title: 'Contratos',
      body: d.contracts.length === 0
        ? <Typography sx={{ fontSize: '0.8rem', color: colors.textSecondary }}>Nenhum contrato cadastrado.</Typography>
        : d.contracts.map((c, i) => <Typography key={i} sx={{ fontSize: '0.85rem' }}>{c.type}{c.expirationDate ? ` · até ${fmtDate(c.expirationDate)}` : ''}</Typography>),
    },
  ];

  const sidePanel = (
    <Box sx={{
      // Em coluna (celular) o flex-basis vira ALTURA — por isso lá é 'auto' e a largura é 100%
      flex: stackPanel ? '0 0 auto' : fullMode ? '1 1 100%' : sideOpen ? '0 0 300px' : '0 0 52px',
      width: stackPanel || fullMode ? '100%' : undefined,
      border: `1px solid ${colors.borderLight}`, borderRadius: 'var(--c4u-radius)', background: '#fff',
      transition: 'flex-basis .2s', overflow: 'hidden', alignSelf: stackPanel ? 'stretch' : 'flex-start',
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: sideOpen ? 'space-between' : 'center', px: sideOpen ? 1.5 : 0, py: 1, borderBottom: `1px solid ${colors.borderLight}` }}>
        {sideOpen && <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: '.03em' }}>Situação do paciente</Typography>}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {sideOpen && (
            <Tooltip title={sideFull ? 'Voltar ao caixa' : 'Ver em tela cheia'} arrow>
              <IconButton size="small" onClick={() => setSideFull((v) => !v)} sx={{ color: colors.primary }}>
                {sideFull ? <FullscreenExit /> : <Fullscreen />}
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title={sideOpen ? 'Recolher' : 'Expandir'} arrow>
            <IconButton size="small" onClick={() => { setSideOpen((v) => !v); setSideFull(false); }} sx={{ color: colors.primary }}>
              {stackPanel ? (sideOpen ? <ExpandLess /> : <ExpandMore />) : (sideOpen ? <ChevronLeft /> : <ChevronRight />)}
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      {sideOpen ? (
      <Box sx={fullMode ? { display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }, gap: 0, '& > *': { borderRight: `1px solid ${colors.borderLight}` } } : undefined}>
      {sections.map((s) => (
        <Box key={s.key} sx={{ borderBottom: `1px solid ${colors.borderLight}`, '&:last-child': { borderBottom: fullMode ? undefined : 0 } }}>
          <Box onClick={() => toggleSection(s.key)} sx={{
            display: 'flex', alignItems: 'center', gap: 1, px: fullMode ? 2 : 1.5, py: fullMode ? 1.25 : 1, cursor: 'pointer',
            background: fullMode ? colors.backgroundSubtle : 'transparent', borderBottom: fullMode ? `1px solid ${colors.borderLight}` : 0,
            '&:hover': { background: fullMode ? colors.backgroundAlt : colors.background },
          }}>
            <Box sx={{ color: colors.primary, display: 'flex', '& svg': { fontSize: fullMode ? 22 : 20 } }}>{s.icon}</Box>
            <Typography sx={{ fontSize: fullMode ? '1rem' : '0.85rem', fontWeight: 600, flex: 1, color: colors.textStrong }}>{s.title}</Typography>
            {s.badge && !isSectionOpen(s.key) && <Typography className="fin-sub" sx={{ fontSize: '0.75rem', color: colors.textSecondary }}>{s.badge}</Typography>}
            {s.action}
            {isSectionOpen(s.key) ? <ExpandLess fontSize="small" sx={{ color: colors.textMuted }} /> : <ExpandMore fontSize="small" sx={{ color: colors.textMuted }} />}
          </Box>
          <Collapse in={fullMode || isSectionOpen(s.key)}>
            {/* Área de dados com rolagem própria: listas longas (pagamentos) não esticam o painel */}
            <Box sx={{
              // No celular a página rola inteira: o bloco cresce até o fim da lista (sem rolagem interna que corta o conteúdo)
              px: fullMode ? 2 : 1.5, pt: fullMode ? 1.5 : 0, pb: fullMode ? 2 : 1.5, maxHeight: stackPanel ? 'none' : fullMode ? 260 : 220, overflowY: stackPanel ? 'visible' : 'auto', overscrollBehavior: 'contain',
              ...(fullMode ? {
                fontSize: '0.95rem',
                '& .MuiTypography-root:not(.fin-keep)': { fontSize: '0.95rem !important' },
                '& .fin-sub': { fontSize: '0.85rem !important' },
              } : {}),
            }}>{s.body}</Box>
          </Collapse>
        </Box>
      ))}
      </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: stackPanel ? 'row' : 'column', alignItems: 'center', gap: 0.5, py: 1, justifyContent: 'center' }}>
          {sections.map((s) => (
            <Tooltip key={s.key} title={s.badge ? `${s.title}: ${s.badge}` : s.title} arrow placement={stackPanel ? 'top' : 'right'}>
              <IconButton size="small" onClick={() => openSide(s.key)} sx={{ color: colors.primary }}>{s.icon}</IconButton>
            </Tooltip>
          ))}
        </Box>
      )}
    </Box>
  );

  /* ---------------- área principal ---------------- */
  const kpi = (label: string, value: string, color: string, detail?: string) => (
    <Box sx={{ flex: '1 1 140px', border: `1px solid ${colors.borderLight}`, borderRadius: 'var(--c4u-radius)', px: 1.5, py: 1 }}>
      <Typography sx={{ fontSize: '0.72rem', color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: '.03em' }}>{label}</Typography>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, flexWrap: 'wrap' }}>
        <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, color }}>{value}</Typography>
        {detail && <Typography sx={{ fontSize: '0.8rem', color: colors.textSecondary }}>{detail}</Typography>}
      </Box>
    </Box>
  );

  const valueOf = (s: FinancialSession): number => {
    if (s.paid) return s.paidValue ?? s.netValue;
    return previewFinal.get(s.id) ?? s.netValue;
  };
  const adjustOf = (s: FinancialSession): number | null => {
    // Paga: o ajuste é o que foi dado no ato (payment_discount, positivo = desconto)
    if (s.paid) return s.paymentDiscount ? round2(-s.paymentDiscount) : null;
    const f = previewFinal.get(s.id);
    return f === undefined ? null : round2(f - s.netValue);
  };

  return (
    <div className="tab-content-section">
      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', mb: '21px' }}>
        <MonthSelect label="Competência" value={competence} onChange={applyCompetence} sx={dateFieldSx} />
        <TextField size="small" type="date" label="De" value={from} onChange={(e) => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} sx={dateFieldSx} />
        <TextField size="small" type="date" label="Até" value={to} onChange={(e) => setTo(e.target.value)} InputLabelProps={{ shrink: true }} sx={dateFieldSx} />
        <TextField select size="small" label="Profissional" value={professional} onChange={(e) => setProfessional(e.target.value)} InputLabelProps={{ shrink: true }} SelectProps={{ displayEmpty: true }} sx={{ ...filterFieldSx, minWidth: { xs: '100%', sm: 180 }, flex: { xs: '1 1 100%', sm: '1 1 180px' } }}>
          <MenuItem value="">Todos</MenuItem>
          {d.professionals.map((p) => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Situação" value={situation} onChange={(e) => setSituation(e.target.value as '' | SituationKey)} InputLabelProps={{ shrink: true }} SelectProps={{ displayEmpty: true }} sx={{ ...filterFieldSx, minWidth: { xs: '100%', sm: 160 }, flex: { xs: '1 1 100%', sm: '0 0 160px' } }}>
          <MenuItem value="">Todas</MenuItem>
          {(Object.keys(SITUATION) as SituationKey[]).map((k) => <MenuItem key={k} value={k}>{SITUATION[k].label}</MenuItem>)}
        </TextField>
        {/* Os dois interruptores empilhados ocupam a largura de um só: assim a barra cabe numa linha ao lado do painel */}
        <Box sx={{ display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
          <FormControlLabel sx={{ ml: 0, mr: 0, height: 22 }} control={<Switch size="small" checked={onlyPackages} onChange={(e) => setOnlyPackages(e.target.checked)} />} label={<Typography sx={{ fontSize: 13 }}>Só pacotes</Typography>} />
          <FormControlLabel sx={{ ml: 0, mr: 0, height: 22 }} control={<Switch size="small" checked={groupByPackage} onChange={toggleGroupByPackage} />} label={<Typography sx={{ fontSize: 13 }}>Agrupar por pacote</Typography>} />
        </Box>
        <ClearFiltersButton onClick={clearFilters} disabled={!hasFilters} />
        <Tooltip title={filtered.length === 0 ? 'Nada para exportar no filtro' : `Exportar para Excel (${filtered.length} sessão(ões) em tela)`} arrow>
          <span style={{ marginLeft: 'auto' }}>
            <IconButton onClick={exportFiltered} disabled={filtered.length === 0 || exporting} sx={filterActionButtonSx} aria-label="Exportar para Excel">
              {exporting ? <CircularProgress size={18} sx={{ color: colors.primary }} /> : <FileDownload />}
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Imprimir extrato" arrow>
          <span>
            <IconButton onClick={(e) => setPrintMenuAnchor(e.currentTarget)} sx={filterActionButtonSx} aria-label="Imprimir extrato" aria-haspopup="menu">
              <Print />
            </IconButton>
          </span>
        </Tooltip>
        <Menu anchorEl={printMenuAnchor} open={Boolean(printMenuAnchor)} onClose={() => setPrintMenuAnchor(null)} disableScrollLock>
          <MenuItem onClick={() => printRows('period')} disabled={sessions.length === 0}>Imprimir todas do período</MenuItem>
          <MenuItem onClick={() => printRows('screen')} disabled={filtered.length === 0}>Imprimir todas da tela</MenuItem>
          <MenuItem onClick={() => printRows('selected')} disabled={selected.length === 0}>Imprimir selecionadas{selected.length ? ` (${selected.length})` : ''}</MenuItem>
        </Menu>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: stackPanel ? 'column' : 'row', gap: 2, alignItems: 'flex-start' }}>
        {sidePanel}

        {!fullMode && (
        <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
          {/* "Pendente até hoje" mora só no painel lateral — aqui confundia com o período filtrado */}
          {/* Caixa ACIMA da lista (Hilton, 05/09, como no Bubble): campos na mesma posição da tela
              antiga — Qtde selecionada, Valor selecionado, Aplicar desconto, Ajuste manual,
              Justificativa, Valor total, Pagamento e Limpar. Rola com a página; só aparece com algo
              pagável na lista ou selecionado. */}
          {(payable.length > 0 || selectedRows.length > 0) && (
          <Box sx={{
            mb: 2, pt: 3, px: 2, pb: 2, background: '#fff', border: `1px solid ${colors.borderLight}`, borderTop: `2px solid ${colors.primary}`,
            borderRadius: 'var(--c4u-radius)', display: 'grid', columnGap: 2, rowGap: '16px',
            gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(6, minmax(0, 1fr))' }, alignItems: 'start',
            // O `minWidth: 150` do estilo de filtro é maior que a coluna do grid: o campo invadia o vizinho
            '& .MuiTextField-root': { minWidth: 0 },
          }}>
            <Typography sx={{ gridColumn: '1 / -1', mt: -1.5, mb: 1, pb: 1, fontSize: '1rem', fontWeight: 600, color: colors.textStrong, borderBottom: `1px solid ${colors.borderLight}` }}>Realizar pagamento</Typography>
            <TextField size="small" label="Qtde selecionada" value={String(selectedRows.length)} InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} sx={{ ...filterFieldSx, '& .MuiInputBase-input': { fontWeight: 600 } }} />
            <TextField size="small" label="Valor selecionado (R$)" value={formatMoney(selectedTotal)} InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} sx={{ ...filterFieldSx, '& .MuiInputBase-input': { fontWeight: 600 } }} />
            <Tooltip arrow title={hasPatientDiscountSelected ? 'Sessão selecionada já tem desconto programado: o percentual não se acumula.' : adjustInUse ? 'Percentual ou ajuste manual: um só.' : ''}>
              <TextField select size="small" label="Aplicar desconto" value={generalDiscountId} onChange={(e) => setGeneralDiscountId(e.target.value)}
                disabled={hasPatientDiscountSelected || adjustInUse || d.generalDiscounts.length === 0}
                InputLabelProps={{ shrink: true }} SelectProps={{ displayEmpty: true }} sx={{ ...filterFieldSx, gridColumn: { xs: '1 / -1', md: 'auto' } }}>
                <MenuItem value="">Nenhum</MenuItem>
                {d.generalDiscounts.map((g) => <MenuItem key={g.id} value={g.id}>{g.title}</MenuItem>)}
              </TextField>
            </Tooltip>
            <TextField size="small" label="Desconto (R$)" value={generalDiscountValue ? formatMoney(generalDiscountValue) : ''} placeholder="0,00" InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} sx={{ ...filterFieldSx, '& .MuiInputBase-input': { color: colors.success, fontWeight: 600 } }} />
            <TextField size="small" label="Ajuste manual (R$)" value={manualAdjust} onChange={(e) => setManualAdjust(moneyKeystroke(e.target.value))}
              onBlur={() => setManualAdjust((v) => moneyBlur(v, true))} placeholder="0,00" disabled={percentInUse}
              InputLabelProps={{ shrink: true }} inputProps={{ maxLength: 14, inputMode: 'decimal' }} error={!!manualError}
              sx={filterFieldSx}
              InputProps={{
                endAdornment: (
                  <Tooltip arrow title={manualError || (percentInUse ? 'Percentual ou ajuste manual: um só.' : '(-) desconta · (+) acresce. Desconto até o total; acréscimo até o dobro. Exige justificativa.')}>
                    <InfoOutlined sx={{ fontSize: 18, color: manualError ? colors.error : colors.textMuted, cursor: 'help' }} />
                  </Tooltip>
                ),
              }} />
            <TextField size="small" label="Valor total (R$)" value={formatMoney(totalToPay)} InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }}
              sx={{ ...filterFieldSx, gridColumn: { xs: '1 / -1', md: 'auto' }, '& .MuiInputBase-input': { color: colors.primary, fontWeight: 700, fontSize: '1.1rem' } }} />

            {needsJustification ? (
              <TextField size="small" label="Justificativa do ajuste" value={manualJustification} onChange={(e) => setManualJustification(e.target.value)}
                InputLabelProps={{ shrink: true }} inputProps={{ maxLength: 200 }} error={!justificationOk} required
                helperText={justificationOk ? '' : `Obrigatória, mínimo ${MIN_JUSTIFICATION} caracteres.`} sx={{ ...filterFieldSx, gridColumn: { xs: '1 / -1', md: 'span 4' }, '& .MuiFormHelperText-root': { mx: 0 } }} />
            ) : (
              <Box sx={{ display: { xs: 'none', md: 'block' }, gridColumn: 'span 4' }} />
            )}
            <Button variant="contained" startIcon={<Payments />} disabled={!canPay} onClick={() => setPayOpen(true)}
              sx={{ textTransform: 'none', fontWeight: 600, height: FIELD_HEIGHT, bgcolor: colors.primary, '&:hover': { bgcolor: colors.primaryHover }, boxShadow: 'none', whiteSpace: 'nowrap' }}>
              Pagamento
            </Button>
            <Button variant="outlined" disabled={selectedRows.length === 0 && !generalDiscountId && !manualAdjust} onClick={resetCashier}
              sx={{ textTransform: 'none', height: FIELD_HEIGHT, borderColor: colors.border, color: colors.textSecondary, whiteSpace: 'nowrap' }}>
              Limpar
            </Button>
          </Box>
          )}

          <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem', mb: 1, px: 1 }}>
            <strong>{filtered.length}</strong> {filtered.length === 1 ? 'sessão no período' : 'sessões no período'}
            {payable.length > 0 && <> · <strong>{payable.length}</strong> em aberto</>}
          </Typography>

          <Box className="c4u-table c4u-table--serial c4u-table--contained">
            <Box className="c4u-table__header">
              <Box className="admin-plans-header-cell" sx={{ flex: '0 0 44px', justifyContent: 'center' }}>
                <Tooltip title="Selecionar todas em aberto" arrow>
                  <Checkbox size="small" checked={allPayableSelected} indeterminate={!allPayableSelected && payable.some((s) => selected.includes(s.id))} disabled={payable.length === 0} onChange={togglePage}
                    sx={{ p: 0.25, color: colors.primary, '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: colors.primary } }} />
                </Tooltip>
              </Box>
              <Box className="admin-plans-header-cell" sx={{ flex: '0 0 104px' }}>Data</Box>
              <Box className="admin-plans-header-cell" sx={{ flex: '2 1 200px', minWidth: 140, justifyContent: 'flex-start', textAlign: 'left' }}>Serviço / Profissional</Box>
              <Box className="admin-plans-header-cell c4u-col--lg" sx={{ flex: '0 0 92px', justifyContent: 'flex-end' }}>Tabela</Box>
              <Box className="admin-plans-header-cell c4u-col--lg" sx={{ flex: '0 0 96px', justifyContent: 'flex-end' }}><Tooltip title="Desconto do paciente (programado no agendamento)" arrow><span>Desc. Pac.</span></Tooltip></Box>
              <Box className="admin-plans-header-cell c4u-col--md" sx={{ flex: '0 0 88px', justifyContent: 'flex-end' }}>Ajuste</Box>
              <Box className="admin-plans-header-cell" sx={{ flex: '0 0 100px', justifyContent: 'flex-end' }}>Valor</Box>
              <Box className="admin-plans-header-cell" sx={{ flex: '0 0 120px', justifyContent: 'center' }}>Situação</Box>
            </Box>
            <Box className="admin-plans-table-body">
              {filtered.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 5, color: colors.textSecondary }}>Nenhuma sessão no período.</Box>
              )}
              {rowsToRender.map(({ session: s, groupHeader }, rowIndex) => {
                const sit = SITUATION[situationOf(s)];
                const isSel = selected.includes(s.id);
                const adj = isSel || s.paid ? adjustOf(s) : null;
                return (
                  <React.Fragment key={s.id}>
                  {groupHeader && (
                    // Título de seção, não mais uma linha: respiro acima dos grupos seguintes e filete no topo.
                    // O subtotal fica na coluna Valor (mesma largura, mesmo recuo) para conferir a soma de olho.
                    <Box className="c4u-table__row" sx={{
                      background: '#e0f5f8 !important', color: '#0b7285', py: 0.75,
                      borderTop: '2px solid #0b7285', mt: rowIndex > 0 ? 2 : 0,
                    }}>
                      <Box sx={{ flex: '1 1 auto', minWidth: 0, display: 'flex', alignItems: 'center', gap: 1, px: '0.5rem', flexWrap: 'wrap' }}>
                        <EventRepeat sx={{ fontSize: 16 }} />
                        <Typography component="span" sx={{ fontSize: 13, fontWeight: 600 }}>{groupHeader.label}</Typography>
                        <Typography component="span" sx={{ fontSize: 12, ml: 'auto' }}>Subtotal</Typography>
                      </Box>
                      <Box sx={{ flex: '0 0 100px', display: 'flex', justifyContent: 'flex-end', px: '0.5rem' }}>
                        <Typography sx={{ fontSize: 14, fontWeight: 700 }}>{brl(groupHeader.subtotal)}</Typography>
                      </Box>
                      <Box aria-hidden sx={{ flex: '0 0 120px', '@media (max-width: 768px)': { display: 'none' } }} />
                    </Box>
                  )}
                  <Box className="c4u-table__row" sx={{ background: isSel ? `${colors.primaryLight} !important` : undefined }}>
                    <Box className="admin-plans-cell" sx={{ display: 'flex', alignItems: 'center', flex: '0 0 44px', justifyContent: 'center' }} data-label="Imprimir">
                      <Tooltip title={s.paid ? 'Já paga' : s.financiallyLocked ? CUTOFF_TIP : 'Selecionar para pagamento'} arrow>
                        <span>
                          <Checkbox size="small" checked={isSel} disabled={s.paid || s.financiallyLocked} onChange={() => toggle(s.id)} sx={{ p: 0.5, color: colors.primary, '&.Mui-checked': { color: colors.primary } }} />
                        </span>
                      </Tooltip>
                    </Box>
                    <Box className="admin-plans-cell" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: '0 0 104px' }} data-label="Data">
                      <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
                        <Typography sx={{ fontSize: 14, fontWeight: 600, lineHeight: 1.2 }}>{fmtDate(s.date)}</Typography>
                        <Typography sx={{ fontSize: 12, color: colors.textSecondary, lineHeight: 1.2 }}>{timeOf(s.date)}</Typography>
                      </Box>
                      <Tooltip title="Editar agendamento" arrow>
                        <IconButton size="small" sx={{ color: colors.primary, p: 0.25, ml: 0.25 }} onClick={() => setEditingScheduleId(s.id)}>
                          <Edit sx={{ fontSize: 15 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <Box className="admin-plans-cell" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', textAlign: 'left', flex: '2 1 200px', minWidth: 140 }} data-label="Serviço">
                      <Typography sx={{ fontSize: 13, overflowWrap: 'anywhere' }}>
                        {s.serviceTypeName || 'Atendimento'}
                        <Box component="span" sx={{ color: colors.textSecondary }}> · {s.professionalName || '—'}</Box>
                        {/* RN-228: marca única de pacote; "sem série" alerta a herança do legado nunca ligada */}
                        {(s.packagePosition != null || s.packageUnlinked) && (
                          <Chip icon={<EventRepeat sx={{ fontSize: '0.85rem !important' }} />} size="small"
                            label={s.packagePosition != null ? `Pacote ${s.packagePosition}/${s.packageSize ?? '?'}` : 'Pacote sem série'}
                            title={s.packagePosition != null ? (s.packageChargeMode === 'attended' ? 'Pacote: cobra só as sessões realizadas' : 'Pacote: cobra o pacote inteiro') : 'Serviço de pacote sem série: sessão avulsa'}
                            sx={{ ml: 0.75, height: 20, fontSize: '0.7rem', fontWeight: 600, verticalAlign: 'middle',
                              bgcolor: s.packagePosition != null ? '#e0f5f8' : '#e2e3e5', color: s.packagePosition != null ? '#0b7285' : '#41464b' }} />
                        )}
                        {s.statusDisplay && (
                          // Mesmo status e cor da agenda — cancelado, falta, remarcar — para o operacional ver o que pede ação
                          <Chip label={s.statusDisplay} size="small" sx={{
                            ml: 0.75, height: 20, fontSize: '0.7rem', fontWeight: 600, verticalAlign: 'middle',
                            bgcolor: s.statusColor || colors.textSecondary, color: colors.white,
                          }} />
                        )}
                      </Typography>
                    </Box>
                    <Box className="admin-plans-cell c4u-col--lg" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', flex: '0 0 92px' }} data-label="Tabela">
                      <Typography sx={{ fontSize: 13, color: colors.textSecondary }}>{brl(s.unitValue)}</Typography>
                    </Box>
                    <Box className="admin-plans-cell c4u-col--lg" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', flex: '0 0 96px' }} data-label="Desc. Pac.">
                      <Typography sx={{ fontSize: 13, color: s.patientDiscount ? colors.success : colors.textMuted }}>{s.patientDiscount ? `- ${brl(s.patientDiscount)}` : '—'}</Typography>
                    </Box>
                    <Box className="admin-plans-cell c4u-col--md" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', flex: '0 0 88px' }} data-label="Ajuste">
                      <Typography sx={{ fontSize: 13, color: adj && adj < 0 ? colors.success : adj && adj > 0 ? colors.warningSoftText : colors.textMuted }}>
                        {adj ? `${adj > 0 ? '+ ' : '- '}${brl(Math.abs(adj))}` : '—'}
                      </Typography>
                    </Box>
                    <Box className="admin-plans-cell" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', flex: '0 0 100px' }} data-label="Valor">
                      <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{brl(valueOf(s))}</Typography>
                    </Box>
                    <Box className="admin-plans-cell" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.25, flex: '0 0 120px' }} data-label="Situação">
                      {s.paid ? (
                        <>
                          <Tooltip title={`Pago${s.paymentDate ? ` em ${fmtDate(s.paymentDate)} ${timeOf(s.paymentDate)}` : ''}`} arrow>
                            <CheckCircle sx={{ fontSize: 18, color: colors.success }} />
                          </Tooltip>
                          <Typography sx={{ fontSize: 12, color: colors.textSecondary }}>{s.paymentDate ? fmtDate(s.paymentDate) : ''}</Typography>
                          {s.paymentId && (
                            <Tooltip title="Ver recibo" arrow>
                              <IconButton size="small" onClick={() => openReceipt(s.paymentId!, false)} sx={{ color: colors.primary, p: 0.25 }}>
                                <Receipt sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </>
                      ) : (
                        <Chip size="small" label={sit.label} sx={{ bgcolor: sit.bg, color: sit.color, fontWeight: 600, height: 22 }} />
                      )}
                    </Box>
                  </Box>
                  </React.Fragment>
                );
              })}
            </Box>
          </Box>

        </Box>
        )}
      </Box>

      <PaymentDialog
        open={payOpen}
        onClose={() => setPayOpen(false)}
        total={totalToPay}
        sessionsCount={selectedRows.length}
        methods={d.paymentMethods}
        injunctionAvailable={!!d.injunction?.activeNow}
        onPay={handlePay}
      />
      <PaymentDialog
        open={editingPayment !== null}
        onClose={() => setEditingPayment(null)}
        total={editingPayment?.total ?? 0}
        sessionsCount={editingPayment?.sessions ?? 0}
        methods={d.paymentMethods}
        injunctionAvailable={false}
        onPay={handleUpdatePayment}
        editing={editingPayment}
      />
      <RevertDialog open={revertTarget !== null} onClose={() => setRevertTarget(null)} onConfirm={handleRevert} />
      <AppointmentEditorDialog
        scheduleId={editingScheduleId}
        patientId={patientId}
        patientName={patientName}
        entityId={user?.entityId ?? ''}
        onClose={() => setEditingScheduleId(null)}
        onChanged={() => { load(); }}
      />
      <DiscountModal
        open={discountModal.open}
        patientId={patientId}
        editing={discountModal.editing}
        professionalTypeOptions={d.professionalTypeOptions}
        onClose={() => setDiscountModal({ open: false, editing: null })}
        onSaved={async () => { showToast('Desconto salvo. Vale para agendamentos criados a partir de agora.', 'success'); await load(); }}
      />
      <PeriodicityModal open={periodicityOpen} patientId={patientId} current={d.periodicity} onClose={() => setPeriodicityOpen(false)}
        onSaved={async () => { showToast('Periodicidade atualizada.', 'success'); await load(); }} />
      <HealthPlanModal open={healthPlanOpen} patientId={patientId} planOptions={d.healthPlanOptions} operatorOptions={d.healthPlanOperatorOptions}
        editing={editingPlan} dataVisible={d.healthPlanDataVisible} onClose={() => { setHealthPlanOpen(false); setEditingPlan(null); }}
        onSaved={async () => { showToast(editingPlan ? 'Vínculo atualizado.' : 'Convênio vinculado.', 'success'); await load(); }} />

      <Toast message={toast.message} type={toast.type} isVisible={toast.isVisible} onClose={hideToast} />
    </div>
  );
};

/* ---------------- modal de pagamento (formas e validação do total) ---------------- */
interface PaymentDialogProps {
  open: boolean;
  onClose: () => void;
  total: number;
  sessionsCount: number;
  methods: PaymentMethodOption[];
  injunctionAvailable: boolean;
  onPay: (method1Id: string | null, value1: number | null, method2Id: string | null, value2: number | null, notes: string, byInjunction: boolean, invoiceUrl: string, printReceipt: boolean, paymentDate: string) => Promise<void>;
  /** Alteração de um pagamento existente: só formas, divisão e observação (RN-543i) */
  editing?: FinancialPayment | null;
}

const PaymentDialog: React.FC<PaymentDialogProps> = ({ open, onClose, total, sessionsCount, methods, injunctionAvailable, onPay, editing = null }) => {
  const [method1, setMethod1] = useState('');
  const [value1, setValue1] = useState('');
  const [split, setSplit] = useState(false);
  const [method2, setMethod2] = useState('');
  const [value2, setValue2] = useState('');
  const [notes, setNotes] = useState('');
  // Data em que o dinheiro entrou (RN-543j). Nasce hoje; a baixa e lancada dias depois e datar
  // tudo como "hoje" jogava a receita para o mes errado no caixa.
  const [paymentDate, setPaymentDate] = useState(todayIso());
  const [invoiceUrl, setInvoiceUrl] = useState('');
  const [byInjunction, setByInjunction] = useState(false);
  // Recibo abre em outra aba só se o caixa quiser (Hilton, 05/09); o recibo continua acessível na lista
  const [printReceipt, setPrintReceipt] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Edição: carrega o gravado ao abrir
  useEffect(() => {
    if (!open || !editing) return;
    setMethod1(editing.method1Id ?? '');
    const hasSecond = !!editing.method2Id;
    setSplit(hasSecond);
    setMethod2(editing.method2Id ?? '');
    setValue1(hasSecond && editing.value1 !== null ? formatMoney(editing.value1) : '');
    setValue2(hasSecond && editing.value2 !== null ? formatMoney(editing.value2) : '');
    setNotes(editing.notes ?? '');
    setInvoiceUrl(editing.invoiceUrl ?? '');
    setByInjunction(false);
    setPaymentDate(editing.date.slice(0, 10));
    setError('');
  }, [open, editing]);

  const num = (v: string) => { const n = parseMoney(v); return Number.isNaN(n) ? 0 : n; };
  const v1 = split ? num(value1) : total;
  const v2 = split ? num(value2) : 0;
  const sum = round2(v1 + v2);
  const sumOk = Math.abs(sum - total) < 0.005;
  const invoiceOk = invoiceUrl.trim() === '' || /^https?:\/\/\S+$/i.test(invoiceUrl.trim());
  // Nunca no futuro: a baixa registra o que ja aconteceu. O servidor recusa igual (RN-543j).
  const dateOk = paymentDate !== '' && paymentDate <= todayIso() && paymentDate >= oldestPaymentIso();
  const valid = dateOk && invoiceOk && (byInjunction || (method1 !== '' && (!split || (method2 !== '' && method2 !== method1)) && sumOk && v1 >= 0 && v2 >= 0));

  const reset = () => { setMethod1(''); setValue1(''); setSplit(false); setMethod2(''); setValue2(''); setNotes(''); setInvoiceUrl(''); setByInjunction(false); setPrintReceipt(true); setPaymentDate(todayIso()); setError(''); };
  const close = () => { if (saving) return; reset(); onClose(); };

  const confirm = async () => {
    setSaving(true);
    setError('');
    try {
      await onPay(
        byInjunction ? null : method1,
        byInjunction ? null : (split ? v1 : total),
        byInjunction || !split ? null : method2,
        byInjunction || !split ? null : v2,
        notes,
        byInjunction,
        invoiceUrl,
        printReceipt,
        paymentDate,
      );
      reset();
    } catch (err) {
      setError(apiError(err, 'Não foi possível registrar o pagamento.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: '12px', boxShadow: '0 10px 30px rgba(0,0,0,0.3)', overflow: 'hidden' } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: colors.primary, color: '#fff', padding: '1.25rem', fontSize: '1.4rem', fontWeight: 600 }}>
        {editing ? 'Alterar pagamento' : 'Pagamento'}
        <IconButton onClick={close} sx={{ color: '#fff', padding: '0.25rem' }} disabled={saving} aria-label="Fechar"><Close /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ padding: '1.5rem !important', paddingTop: '1.5rem !important' }}>
        {/* Valor ACIMA da descrição (pedido do Hilton, 07/09): lado a lado, o número competia
            com o texto e encostava no seletor de forma de pagamento. Em cima, ele é a primeira
            coisa que se lê — que é o que importa ao conferir um pagamento. */}
        <Box sx={{ mb: 2 }}>
          <Typography sx={{ fontSize: '1.75rem', fontWeight: 700, color: colors.primary, lineHeight: 1.15 }}>
            {brl(total)}
          </Typography>
          <Typography sx={{ color: colors.textSecondary, fontSize: '0.85rem', mt: 0.25 }}>
            {editing ? `${sessionsCount} sessão(ões) · sessões e total não mudam` : `${sessionsCount} sessão(ões) selecionada(s)`}
          </Typography>
        </Box>
        {injunctionAvailable && (
          <FormControlLabel sx={{ mb: 1 }} control={<Switch checked={byInjunction} onChange={(e) => setByInjunction(e.target.checked)} />}
            label={<Typography sx={{ fontSize: '0.9rem' }}>Pago por liminar judicial (sem forma de pagamento)</Typography>} />
        )}
        {!byInjunction && (
          <>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: split ? '2fr 1fr' : '1fr' }, columnGap: 1.5, rowGap: 2 }}>
              <TextField select size="small" label="Forma de pagamento" value={method1} onChange={(e) => setMethod1(e.target.value)} InputLabelProps={{ shrink: true }} SelectProps={{ displayEmpty: true }}>
                <MenuItem value="" disabled>Selecione</MenuItem>
                {methods.map((m) => <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>)}
              </TextField>
              {split && <TextField size="small" label="Valor (R$)" value={value1} onChange={(e) => setValue1(moneyKeystroke(e.target.value))} onBlur={() => setValue1((v) => moneyBlur(v, false))} placeholder="0,00" InputLabelProps={{ shrink: true }} inputProps={{ maxLength: 14, inputMode: 'decimal' }} />}
              {split && (
                <>
                  <TextField select size="small" label="Segunda forma" value={method2} onChange={(e) => setMethod2(e.target.value)} InputLabelProps={{ shrink: true }} SelectProps={{ displayEmpty: true }}
                    error={method2 !== '' && method2 === method1} helperText={method2 !== '' && method2 === method1 ? 'Escolha uma forma diferente da primeira.' : ''}>
                    <MenuItem value="" disabled>Selecione</MenuItem>
                    {methods.map((m) => <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>)}
                  </TextField>
                  <TextField size="small" label="Valor (R$)" value={value2} onChange={(e) => setValue2(moneyKeystroke(e.target.value))} onBlur={() => setValue2((v) => moneyBlur(v, false))} placeholder="0,00" InputLabelProps={{ shrink: true }} inputProps={{ maxLength: 14, inputMode: 'decimal' }} />
                </>
              )}
            </Box>
            <Button size="small" onClick={() => { setSplit((v) => !v); if (split) { setMethod2(''); setValue2(''); setValue1(''); } }} sx={{ textTransform: 'none', mt: 1, color: colors.primary }}>
              {split ? 'Usar uma forma só' : 'Dividir em duas formas'}
            </Button>
            {split && (
              <Typography sx={{ fontSize: '0.8rem', mt: 0.5, color: sumOk ? colors.success : colors.error }}>
                {sumOk ? 'As duas formas somam o total.' : `As formas somam ${brl(sum)}; faltam ${brl(round2(total - sum))} para o total.`}
              </Typography>
            )}
          </>
        )}
        <TextField
          size="small"
          type="date"
          label="Data do pagamento"
          value={paymentDate}
          onChange={(e) => setPaymentDate(e.target.value)}
          fullWidth
          InputLabelProps={{ shrink: true }}
          inputProps={{ max: todayIso(), min: oldestPaymentIso() }}
          error={!dateOk}
          helperText={
            dateOk
              ? 'Quando o dinheiro entrou. Deixe hoje se a baixa é do dia.'
              : paymentDate > todayIso()
                ? 'A data não pode ser futura.'
                : `A data não pode ser anterior a ${PAYMENT_DATE_MAX_PAST_DAYS} dias.`
          }
          sx={{ mt: 2 }}
        />
        <TextField size="small" label="Link da nota fiscal (NF)" value={invoiceUrl} onChange={(e) => setInvoiceUrl(e.target.value)} fullWidth placeholder="https://…"
          InputLabelProps={{ shrink: true }} inputProps={{ maxLength: 500, inputMode: 'url' }} error={!invoiceOk} helperText={invoiceOk ? 'Opcional; pode ser preenchido depois em Alterar pagamento.' : 'Use um endereço completo (http:// ou https://).'} sx={{ mt: 2 }} />
        <TextField size="small" label="Observação" value={notes} onChange={(e) => setNotes(e.target.value)} fullWidth multiline minRows={2} InputLabelProps={{ shrink: true }} inputProps={{ maxLength: 500 }} sx={{ mt: 2 }} />
        {!editing && (
          <FormControlLabel sx={{ mt: 1, mr: 0 }} label={<Typography sx={{ fontSize: '0.9rem' }}>Imprimir recibo ao confirmar</Typography>}
            control={<Checkbox size="small" checked={printReceipt} onChange={(e) => setPrintReceipt(e.target.checked)} sx={{ color: colors.primary, '&.Mui-checked': { color: colors.primary } }} />} />
        )}
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        <Typography sx={{ fontSize: '0.78rem', color: colors.textSecondary, mt: 1.5 }}>
          {editing
            ? 'A data, a forma de pagamento, a divisão dos valores, o link da NF e a observação são alterados. Para mudar as sessões ou o valor, estorne e registre de novo.'
            : printReceipt
              ? 'Ao confirmar, o pagamento é registrado, as sessões recebem baixa e o recibo abre para impressão no modelo da clínica.'
              : 'Ao confirmar, o pagamento é registrado e as sessões recebem baixa. O recibo fica disponível em Pagamentos realizados.'}
        </Typography>
      </DialogContent>
      <DialogActions sx={{ padding: '1rem 1.5rem', gap: 1 }}>
        <Button onClick={close} variant="outlined" disabled={saving} sx={{ textTransform: 'none', borderColor: colors.border, color: colors.textSecondary }}>Cancelar</Button>
        <Button variant="contained" disabled={!valid || saving} onClick={confirm} startIcon={saving ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <Receipt />}
          sx={{ textTransform: 'none', fontWeight: 600, bgcolor: colors.success, '&:hover': { bgcolor: colors.successHover }, boxShadow: 'none' }}>
          {saving ? 'Salvando…' : editing ? 'Salvar alteração' : `Pagar ${brl(total)}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/* ---------------- estorno com justificativa ---------------- */
const RevertDialog: React.FC<{ open: boolean; onClose: () => void; onConfirm: (justification: string) => Promise<void> }> = ({ open, onClose, onConfirm }) => {
  const [justification, setJustification] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const ok = justification.trim().length >= 5;
  const close = () => { if (saving) return; setJustification(''); setError(''); onClose(); };
  const confirm = async () => {
    setSaving(true);
    setError('');
    try { await onConfirm(justification.trim()); setJustification(''); } catch (err) { setError(apiError(err, 'Não foi possível estornar.')); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="xs" PaperProps={{ sx: { borderRadius: '12px', boxShadow: '0 10px 30px rgba(0,0,0,0.3)', overflow: 'hidden' } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: colors.error, color: '#fff', padding: '1.25rem', fontSize: '1.4rem', fontWeight: 600 }}>
        Estornar pagamento
        <IconButton onClick={close} sx={{ color: '#fff', padding: '0.25rem' }} disabled={saving} aria-label="Fechar"><Close /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ padding: '1.5rem !important', paddingTop: '1.5rem !important' }}>
        <Typography sx={{ fontSize: '0.9rem', mb: 2 }}>As sessões deste recibo voltam a ficar em aberto. O recibo fica marcado como estornado.</Typography>
        <TextField size="small" label="Justificativa" value={justification} onChange={(e) => setJustification(e.target.value)} fullWidth multiline minRows={2} required
          InputLabelProps={{ shrink: true }} inputProps={{ maxLength: 300 }} error={justification !== '' && !ok} helperText={justification !== '' && !ok ? 'Mínimo 5 caracteres.' : ''} />
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions sx={{ padding: '1rem 1.5rem', gap: 1 }}>
        <Button onClick={close} variant="outlined" disabled={saving} sx={{ textTransform: 'none', borderColor: colors.border, color: colors.textSecondary }}>Cancelar</Button>
        <Button variant="contained" disabled={!ok || saving} onClick={confirm} sx={{ textTransform: 'none', fontWeight: 600, bgcolor: colors.error, '&:hover': { bgcolor: colors.errorHover }, boxShadow: 'none' }}>
          {saving ? 'Estornando…' : 'Confirmar estorno'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PatientFinancialTab;
